// client/src/components/AvailabilityForm.js
import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import ApiAvailability from '../../api/ApiAvailability.js'
import services from '../../services'
import {
  generateRRule,
  describeRecurrence,
  countOccurrences,
} from '../../utils/recurrence.js'
import DayPicker from './DayPicker.js'
import './AvailabilityForm.css'

export default function AvailabilityForm() {
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [divisionInfo, setDivisionInfo] = useState(null)
  const [availableLeagues, setAvailableLeagues] = useState([])
  const [selectedLeague, setSelectedLeague] = useState('')
  const [selectedDivision, setSelectedDivision] = useState(null)
  const [loadingLeagues, setLoadingLeagues] = useState(false)
  const [applyTo, setApplyTo] = useState('team')

  // Recurrence state
  const [isRecurring, setIsRecurring] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [interval, setInterval] = useState(1)
  const [endType, setEndType] = useState('league')
  const [customEndDate, setCustomEndDate] = useState('')

  const { leagueId } = useParams()
  const navigate = useNavigate()

  const loadDivisionForLeague = useCallback(async (leagueIdToLoad) => {
    try {
      const divResponse = await services.league.getMyDivision(leagueIdToLoad)
      if (divResponse.success) {
        setSelectedDivision(divResponse.data)
      }
    } catch (err) {
      console.error('Failed to load division info:', err)
    }
  }, [])

  const loadUserLeagues = useCallback(async () => {
    try {
      setLoadingLeagues(true)
      const response = await services.league.getMyLeagues()
      if (response.success) {
        const leagues = response.data || []
        setAvailableLeagues(leagues)

        // If user has only one league, auto-select it
        if (leagues.length === 1) {
          setSelectedLeague(leagues[0].league_id.toString())
          await loadDivisionForLeague(leagues[0].league_id)
        }
      }
    } catch (err) {
      console.error('Failed to load user leagues:', err)
      setMessage('Failed to load your leagues. Please try again.')
      setMessageType('error')
    } finally {
      setLoadingLeagues(false)
    }
  }, [loadDivisionForLeague])

  const loadDivisionInfo = useCallback(async () => {
    try {
      const divResponse = await services.league.getMyDivision(leagueId)
      if (divResponse.success) {
        setDivisionInfo(divResponse.data)
      }
    } catch (err) {
      console.error('Failed to load division info:', err)
    }
  }, [leagueId])

  useEffect(() => {
    if (leagueId) {
      loadDivisionInfo()
    } else {
      // No league context - load user's leagues for selection
      loadUserLeagues()
    }
  }, [leagueId, loadDivisionInfo, loadUserLeagues])

  const handleLeagueChange = async (e) => {
    const newLeagueId = e.target.value
    setSelectedLeague(newLeagueId)
    setSelectedDivision(null)

    if (newLeagueId) {
      await loadDivisionForLeague(parseInt(newLeagueId))
    }
  }

  // Generate hourly options from 6am to 10pm
  const timeOptions = []
  for (let hour = 6; hour <= 22; hour++) {
    const time24 = `${hour.toString().padStart(2, '0')}:00`
    const time12 =
      hour > 12
        ? `${hour - 12}:00 PM`
        : hour === 12
          ? '12:00 PM'
          : `${hour}:00 AM`
    timeOptions.push({ value: time24, label: time12 })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setMessage('')

    // Basic validation
    if (!date || !startTime || !endTime) {
      setMessage('Please fill in all fields')
      setMessageType('error')
      setSubmitting(false)
      return
    }

    if (startTime >= endTime) {
      setMessage('End time must be after start time')
      setMessageType('error')
      setSubmitting(false)
      return
    }

    // Validate recurring availability
    if (isRecurring && selectedDays.length === 0) {
      setMessage('Please select at least one day for recurring availability')
      setMessageType('error')
      setSubmitting(false)
      return
    }

    if (isRecurring && endType === 'date' && !customEndDate) {
      setMessage('Please select an end date')
      setMessageType('error')
      setSubmitting(false)
      return
    }

    // Ensure we have league and division context
    let currentLeagueId, currentDivisionId, leagueEndDate, currentDivision

    if (leagueId) {
      // League-specific route
      if (!divisionInfo) {
        setMessage('Loading league information, please try again in a moment')
        setMessageType('error')
        setSubmitting(false)
        return
      }
      currentLeagueId = parseInt(leagueId)
      currentDivisionId = divisionInfo.division_id
      leagueEndDate = divisionInfo.league_end_date
      currentDivision = divisionInfo
    } else {
      // Global route - need league selection
      if (!selectedLeague || !selectedDivision) {
        setMessage('Please select a league to add availability for')
        setMessageType('error')
        setSubmitting(false)
        return
      }
      currentLeagueId = parseInt(selectedLeague)
      currentDivisionId = selectedDivision.division_id
      currentDivision = selectedDivision

      // Find the selected league to get its end date
      const league = availableLeagues.find(
        (l) => l.league_id === parseInt(selectedLeague),
      )
      leagueEndDate = league?.league_end_date
    }

    // Validate custom end date doesn't exceed league end
    if (isRecurring && endType === 'date' && customEndDate && leagueEndDate) {
      const customEnd = new Date(`${customEndDate}T23:59:59Z`)
      if (customEnd > new Date(leagueEndDate)) {
        setMessage('End date cannot exceed league end date')
        setMessageType('error')
        setSubmitting(false)
        return
      }
    }

    try {
      // Combine date and time into ISO timestamps
      const startDateTime = `${date}T${startTime}:00Z`
      const endDateTime = `${date}T${endTime}:00Z`

      const availabilityData = {
        start_time: startDateTime,
        end_time: endDateTime,
        league_id: currentLeagueId,
        division_id: currentDivisionId,
      }

      // Add recurrence fields if recurring
      if (isRecurring) {
        const startDate = new Date(startDateTime)
        let endDate

        if (endType === 'league' && leagueEndDate) {
          endDate = new Date(leagueEndDate)
        } else if (endType === 'date' && customEndDate) {
          endDate = new Date(`${customEndDate}T23:59:59Z`)
        }

        try {
          const rruleString = generateRRule({
            days: selectedDays,
            interval,
            startDate,
            endDate,
          })

          availabilityData.is_recurring = true
          availabilityData.recurrence_rule = rruleString
        } catch (error) {
          setMessage(`Error generating recurrence rule: ${error.message}`)
          setMessageType('error')
          setSubmitting(false)
          return
        }
      }

      const isDoublesLeague = currentDivision?.league_type === 'Doubles'
      const currentPlayer = (currentDivision?.roster || []).find(
        (player) => Number(player.id) === Number(currentDivision?.player_id),
      )
      const teamId = currentPlayer?.partner?.team_id || null

      const result =
        isDoublesLeague && applyTo === 'team'
          ? await ApiAvailability.setTeamAvailability({
              ...availabilityData,
              team_id: teamId,
            })
          : await ApiAvailability.create(availabilityData)

      if (result.success) {
        setMessage(
          isDoublesLeague && applyTo === 'team'
            ? 'Team availability updated.'
            : 'Your availability saved.',
        )
        setMessageType('success')
        // Reset form
        setDate('')
        setStartTime('')
        setEndTime('')
        setIsRecurring(false)
        setSelectedDays([])
        setInterval(1)
        setEndType('league')
        setCustomEndDate('')

        // Navigate back after short delay
        setTimeout(() => {
          if (leagueId) {
            navigate(`/leagues/${leagueId}/availability`)
          } else if (selectedLeague) {
            navigate(`/leagues/${selectedLeague}/availability`)
          } else {
            navigate('/availability')
          }
        }, 1500)
      } else {
        setMessage(result.error || 'Failed to add availability')
        setMessageType('error')
      }
    } catch (error) {
      console.error('Error adding availability:', error)
      setMessage('An error occurred while adding availability')
      setMessageType('error')
    } finally {
      setSubmitting(false)
    }
  }

  // Generate preview text for recurring availability
  const getPreviewText = () => {
    if (!isRecurring || selectedDays.length === 0 || !startTime || !endTime) {
      return null
    }

    let leagueEndDate
    if (leagueId && divisionInfo) {
      leagueEndDate = divisionInfo.league_end_date
    } else if (selectedLeague) {
      const league = availableLeagues.find(
        (l) => l.league_id === parseInt(selectedLeague),
      )
      leagueEndDate = league?.league_end_date
    }

    const startDate = new Date(date ? `${date}T${startTime}:00Z` : Date.now())
    let endDate

    if (endType === 'league' && leagueEndDate) {
      endDate = new Date(leagueEndDate)
    } else if (endType === 'date' && customEndDate) {
      endDate = new Date(`${customEndDate}T23:59:59Z`)
    }

    const description = describeRecurrence(selectedDays, interval, endDate)

    // Format times for display
    const formatTime = (time24) => {
      const [hour] = time24.split(':')
      const h = parseInt(hour)
      return h > 12 ? `${h - 12}:00 PM` : h === 12 ? '12:00 PM' : `${h}:00 AM`
    }

    const startTimeFormatted = formatTime(startTime)
    const endTimeFormatted = formatTime(endTime)

    let preview = `${description} from ${startTimeFormatted} - ${endTimeFormatted}`

    // Add occurrence count if we have all the data
    if (date && endDate) {
      try {
        const count = countOccurrences({
          days: selectedDays,
          interval,
          startDate,
          endDate,
        })
        if (count > 0) {
          preview += ` (${count} time slot${count !== 1 ? 's' : ''} will be created)`
        }
      } catch (error) {
        console.error('Error counting occurrences:', error)
      }
    }

    return preview
  }

  return (
    <PageLayout title="Add Availability">
      <div className="availability-form-container">
        <form onSubmit={handleSubmit} className="availability-form">
          {/* League selector for global route */}
          {!leagueId && (
            <div className="form-group">
              <label htmlFor="league" className="form-label">
                League *
              </label>
              {loadingLeagues ? (
                <p>Loading your leagues...</p>
              ) : availableLeagues.length === 0 ? (
                <div className="no-leagues-message">
                  <p>You are not registered in any leagues.</p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate('/my-leagues')}
                  >
                    View Available Leagues
                  </button>
                </div>
              ) : (
                <>
                  <select
                    id="league"
                    value={selectedLeague}
                    onChange={handleLeagueChange}
                    className="form-control"
                    required
                  >
                    <option value="">Select a league...</option>
                    {availableLeagues.map((league) => (
                      <option key={league.league_id} value={league.league_id}>
                        {league.league_name}
                      </option>
                    ))}
                  </select>
                  {selectedLeague && selectedDivision && (
                    <small className="form-text text-muted">
                      Division: {selectedDivision.division_name}
                    </small>
                  )}
                </>
              )}
            </div>
          )}

          {/* Apply-to scope toggle (doubles only) */}
          {((leagueId && divisionInfo?.league_type === 'Doubles') ||
            (!leagueId && selectedDivision?.league_type === 'Doubles')) && (
            <div className="form-group">
              <label className="form-label">Apply to</label>
              <div className="availability-form-scope-toggle">
                <button
                  type="button"
                  className={applyTo === 'team' ? 'active' : ''}
                  onClick={() => setApplyTo('team')}
                >
                  Both of us
                </button>
                <button
                  type="button"
                  className={applyTo === 'individual' ? 'active' : ''}
                  onClick={() => setApplyTo('individual')}
                >
                  Just me
                </button>
              </div>
              {applyTo === 'individual' && (
                <div className="availability-form-scope-warning">
                  <strong>Heads up</strong>
                  <span>
                    Your partner still needs separate availability when this is
                    just for you.
                  </span>
                  <button type="button" onClick={() => setApplyTo('team')}>
                    Switch back to "Both of us"
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Date field */}
          <div className="form-group">
            <label htmlFor="date" className="form-label">
              Date
            </label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-input"
              min={new Date().toISOString().split('T')[0]} // Prevent past dates
            />
          </div>

          <div className="time-row">
            <div className="form-group">
              <label htmlFor="startTime" className="form-label">
                Start Time
              </label>
              <select
                id="startTime"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="form-select"
              >
                <option value="">Select start time</option>
                {timeOptions.map((time) => (
                  <option key={time.value} value={time.value}>
                    {time.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="endTime" className="form-label">
                End Time
              </label>
              <select
                id="endTime"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="form-select"
              >
                <option value="">Select end time</option>
                {timeOptions.map((time) => (
                  <option key={time.value} value={time.value}>
                    {time.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Recurrence Section */}
          <div className="recurrence-section">
            <div className="form-group">
              <label className="form-label">Availability Type</label>
              <div className="recurrence-toggle">
                <label className="toggle-option">
                  <input
                    type="radio"
                    name="recurrence-type"
                    checked={!isRecurring}
                    onChange={() => setIsRecurring(false)}
                  />
                  <span>One-time availability</span>
                </label>
                <label className="toggle-option">
                  <input
                    type="radio"
                    name="recurrence-type"
                    checked={isRecurring}
                    onChange={() => setIsRecurring(true)}
                  />
                  <span>Repeat weekly</span>
                </label>
              </div>
            </div>

            {isRecurring && (
              <div className="recurrence-options">
                <div className="form-group">
                  <label className="form-label">On these days</label>
                  <DayPicker
                    selectedDays={selectedDays}
                    onChange={setSelectedDays}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interval" className="form-label">
                    Repeat every
                  </label>
                  <div className="interval-control">
                    <select
                      id="interval"
                      value={interval}
                      onChange={(e) => setInterval(parseInt(e.target.value))}
                      className="form-select interval-select"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                    <span className="interval-label">week(s)</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Ends</label>
                  <div className="end-options">
                    <label className="end-option">
                      <input
                        type="radio"
                        name="end-type"
                        checked={endType === 'league'}
                        onChange={() => setEndType('league')}
                      />
                      <span>
                        When league ends
                        {((leagueId && divisionInfo?.league_end_date) ||
                          (selectedLeague &&
                            availableLeagues.find(
                              (l) => l.league_id === parseInt(selectedLeague),
                            )?.league_end_date)) && (
                          <span className="end-date-hint">
                            {' '}
                            (
                            {new Date(
                              leagueId
                                ? divisionInfo.league_end_date
                                : availableLeagues.find(
                                    (l) =>
                                      l.league_id === parseInt(selectedLeague),
                                  )?.league_end_date,
                            ).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                            )
                          </span>
                        )}
                      </span>
                    </label>
                    <label className="end-option">
                      <input
                        type="radio"
                        name="end-type"
                        checked={endType === 'date'}
                        onChange={() => setEndType('date')}
                      />
                      <span>On specific date</span>
                    </label>
                    {endType === 'date' && (
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="form-input custom-end-date"
                        data-testid="custom-end-date"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    )}
                  </div>
                </div>

                {getPreviewText() && (
                  <div className="recurrence-preview">
                    <strong>Preview:</strong> {getPreviewText()}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Adding...' : 'Add Availability'}
          </button>

          {message && <div className={`message ${messageType}`}>{message}</div>}
        </form>
      </div>
    </PageLayout>
  )
}
